import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { LatestIdentification, confidenceText, levelText, theCall } from '../dna/DnaIdentificationsPanel';
import { srOnly } from '../organic/motion';
import { PLATE_STATUS_COLORS, SLANT_STATUS_COLORS } from '../../constants/statusColors';
import { formatDay } from '../SlantStoragePanel';
import { STAGES, stateStyle } from './stages';
import * as S from '../specimens/styles';

/*
 * The small pieces the hub's tables and expanded rows are drawn from: a
 * status pill, a row's pipeline strip, an identification. Each says its
 * meaning in words as well as colour.
 */

const pillBase = {
  display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  whiteSpace: 'nowrap', border: '1px solid currentColor',
};

export function PlatePill({ status, t }) {
  const c = PLATE_STATUS_COLORS[status] || PLATE_STATUS_COLORS.failed;
  return <span style={{ ...pillBase, background: c.background, color: c.color }}>{t(`plates:plateStatus.${status}`)}</span>;
}

export function SlantPill({ status, t }) {
  const c = SLANT_STATUS_COLORS[status] || SLANT_STATUS_COLORS.discarded;
  return <span style={{ ...pillBase, background: c.background, color: c.color }}>{t(`slants:slantStatus.${status}`)}</span>;
}

export function ReviewDue({ t }) {
  return (
    <span style={{ ...pillBase, background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' }}
      title={t('tip.reviewDue')} data-testid="hub-review-due">
      ⏰ {t('bank.reviewDue')}
    </span>
  );
}

/** "ISO-…" in a fixed-width face, kept left to right in Hebrew too. On one
 *  line, unless `wrap` lets a long code break where its cell is narrow (the
 *  table's frozen ID column, a phone's card). */
export function Code({ children, strong = false, wrap = false }) {
  if (!children) return '—';
  const breaking = wrap ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : { whiteSpace: 'nowrap' };
  return <bdi dir="ltr" style={{ ...S.mono, fontWeight: strong ? 700 : 400, ...breaking }}>{children}</bdi>;
}

export function Taxon({ name }) {
  if (!name) return '—';
  return <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{name}</bdi>;
}

/** The row's five stages as one strip of boxes: glyph, border and words,
 *  not colour alone. A stage the server sent as null is one this reader is
 *  not shown (no lab visibility: culture, DNA, and a bank that is not the
 *  specimen's own storage) -- said as unknown, never as "Not started". */
export function PipelineStrip({ pipeline, t, onPress = null }) {
  return (
    <ol className="hub-strip" aria-label={t('columns.pipeline')} data-testid="hub-strip">
      {STAGES.map((stage) => {
        const raw = pipeline?.[stage];
        const state = pipeline && raw === null ? 'unknown' : raw?.state || 'pending';
        const look = stateStyle(state);
        const said = state === 'unknown' ? t('state.unknown')
          : raw?.detail ? t(`detail.${stage}.${raw.detail}`) : t('state.pending');
        const words = `${t(`stage.${stage}`)}: ${said}`;
        const style = { background: look.bg, color: look.fg, borderStyle: look.border };
        // With a handler, each cell opens that stage's peek (IsolatePeek).
        return (
          <li key={stage} title={words} data-state={state} style={onPress ? { ...style, padding: 0 } : style}>
            {onPress ? (
              <button type="button" onClick={() => onPress(stage)} aria-label={words}
                data-testid={`hub-strip-${stage}`}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit',
                  padding: '0 6px', minHeight: 24 }}>
                <span aria-hidden="true">{look.glyph}</span>
              </button>
            ) : (
              <>
                <span aria-hidden="true">{look.glyph}</span>
                <span style={srOnly}>{words}</span>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The DNA catalogue's t, for LatestIdentification: its words
 * (identifications.*) are the DNA page's, and the hub's own t (isolates)
 * would show them as raw keys. This t resolves in dna only. `isolates` is
 * listed after it because keysExist checks a file's literal keys against
 * its first useTranslation, and this file's own keys are said with the
 * hub's t.
 */
export function useDnaT() {
  return useTranslation(['dna', 'isolates']).t;
}

const sameName = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * The current identification: a DNA result as the DNA page says it
 * (LatestIdentification), anything else -- the collection's own
 * identification history -- in its own words: name, rank, how sure, and
 * whether an expert approved it.
 *
 * `taxonName` is the name the row already shows beside it: when the
 * identification names the same taxon it is not said twice, only how far
 * and how sure.
 */
export function IdentificationCell({ latest, t, taxonName = null }) {
  const tDna = useDnaT();
  if (!latest) return '—';
  if (latest.source === 'dna') {
    const call = theCall(latest, tDna);
    const rest = [call.showLevel ? levelText(latest.recommended_level, tDna) : '',
      confidenceText(latest.confidence, tDna)].filter(Boolean);
    if (sameName(call.name, taxonName) && rest.length) {
      return (
        <span>
          <span style={S.hint}>{t('ident.dna')} </span>
          <span data-testid="dna-latest-identification">{rest.join(' · ')}</span>
        </span>
      );
    }
    return (
      <span>
        <span style={S.hint}>{t('ident.dna')} </span>
        <LatestIdentification latest={latest} t={tDna} />
      </span>
    );
  }
  const parts = [
    latest.taxonomic_rank ? t(`common:rank.${latest.taxonomic_rank}`) : '',
    latest.confidence ? t(`common:confidence.${latest.confidence}`) : '',
  ].filter(Boolean);
  const named = !sameName(latest.taxon_name, taxonName);
  return (
    <span data-testid="hub-ident-collection">
      {named && <Taxon name={latest.taxon_name} />}
      {parts.length > 0 && <span style={S.hint}>{named ? ' · ' : ''}{parts.join(' · ')}</span>}
      {latest.expert_approval && (
        <span style={{ marginInlineStart: 6, color: 'var(--c-success-fg)', fontWeight: 600 }}
          title={t('tip.approved')}>✓ {t('ident.approved')}</span>
      )}
    </span>
  );
}

/** "Freezer −80 °C · F1 · 3/12/C7": the unit in the reader's words, its
 *  Freezer ID, then rack, box and well. '' outside a unit. */
export function bankPlace(loc, t) {
  if (!loc?.storage_unit) return '';
  const position = [loc.storage_rack, loc.storage_box, loc.storage_well].filter(Boolean).join('/');
  return [t(`specimens:storage.units.${loc.storage_unit}`), loc.storage_unit_label, position]
    .filter(Boolean).join(' · ');
}

/**
 * A date the server sent as YYYY-MM-DD or an ISO moment, in the reader's
 * format. A bare date is a calendar day (no zone); a moment -- a check's
 * check_date, a note's time, an intake's received_at, serialised in the
 * database's zone (UTC) -- is shown as the reader's local day, so a check
 * logged at 01:30 in Israel is that day, not the UTC day before. A camera's
 * own EXIF moment ("2026:02:15 10:22:33", a micrograph's captured_at) has no
 * zone: its date is the camera's calendar day.
 */
export function dayText(value) {
  if (!value) return '—';
  const s = String(value);
  const exif = /^(\d{4}):(\d{2}):(\d{2})(?:[ T]|$)/.exec(s);
  const day = exif ? `${exif[1]}-${exif[2]}-${exif[3]}` : s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(day)) return formatDay(day) || day;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString();
}

export function Day({ value }) {
  return dayText(value);
}

/** A measured value with a unit ("22.5 °C", "80% RH"): kept left to right,
 *  so right to left it does not read "C° 22.5". */
export function Ltr({ children }) {
  return <bdi dir="ltr">{children}</bdi>;
}

/** Nodes joined by " · ", skipping empty ones. */
export function Joined({ parts }) {
  const shown = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== '');
  return shown.map((p, i) => <Fragment key={i}>{i > 0 && ' · '}{p}</Fragment>);
}
